import endpoints from '@dogfy/services/apiEndpoints'
import { isTokenExpired } from '@dogfy/lib/utils'

import axios from 'axios'

axios.defaults.baseURL = import.meta.env.VUE_APP_API_URL
axios.defaults.headers.common.Authorization = `Bearer ${import.meta.env.VUE_APP_GUEST_TOKEN}`

const ApiService = {

  // TODO: Remove once new-cust is deployed to production environment.
  init (authStore, router) {
    let controller = new AbortController()

    // Request interceptor
    axios.interceptors.request.use(async (config) => {
      config = { ...config, signal: controller.signal }
      const isPublicRoute = router.currentRoute.value.meta.public
      const { refreshToken, accessToken } = authStore
      const requestUrl = config.url

      if (isPublicRoute) return config

      if (!refreshToken || isTokenExpired(refreshToken) || !accessToken) {
        controller.abort()
        return config
      }

      if (isTokenExpired(accessToken) && requestUrl !== endpoints.auth.refreshToken) {
        const newAccessToken = await authStore.updateToken()
        if (!newAccessToken) {
          controller.abort()
          return config
        }
        config.headers.Authorization = `Bearer ${newAccessToken}`
      }

      return config
    }, (error) => Promise.reject(error))

    // Response interceptor
    axios.interceptors.response.use(response => response, async (error) => {
      const { refreshToken, accessToken } = authStore
      const isAccessTokenAndExpired = accessToken && isTokenExpired(accessToken)
      const isRefreshTokenAndExpired = refreshToken && isTokenExpired(refreshToken)

      // We also need to re-check token expiration, because it may have expired between the time we check it in the
      // request interceptor and the time the API receives the request and checks the token in its auth middleware.
      if (error.code === 'ERR_CANCELED' || (isRefreshTokenAndExpired && error.config.url === endpoints.auth.refreshToken)) {
        authStore.resetTokens()
        controller = new AbortController()
        await router.push({ name: 'login' })
        error = new Error('Logged out due a token expiration.')
      } else if (refreshToken && isAccessTokenAndExpired) {
        const newAccessToken = await authStore.updateToken()
        if (newAccessToken) {
          error.config.headers.Authorization = `Bearer ${newAccessToken}`
          return axios.request(error.config)
        }
      }

      return Promise.reject(error)
    })
  },

  setRequestInterceptor (request, error) {
    axios.interceptors.request.use(request, error)
  },

  setResponseInterceptor (request, error) {
    axios.interceptors.response.use(request, error)
  },

  setBaseURL (baseURL) {
    axios.defaults.baseURL = baseURL
  },

  setAuthHeader (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`
  },

  removeAuthHeader () {
    axios.defaults.headers.common.Authorization = `Bearer ${import.meta.env.VUE_APP_GUEST_TOKEN}`
  },

  get (resource, params) {
    return axios.get(resource, { params })
  },

  post (resource, data, opts) {
    return axios.post(resource, data, opts)
  },

  put (resource, data, opts) {
    return axios.put(resource, data, opts)
  },

  patch (resource, data, opts) {
    return axios.patch(resource, data, opts)
  },

  delete (resource, data, opts) {
    return axios.delete(resource, data, opts)
  },

  /*
   Perform a custom Axios request.

   data is an object containing the following properties:
   - method
   - url
   - data ... request payload
   - auth (optional)
     - username
     - password
  */
  customRequest (data) {
    return axios(data)
  },
}

export default ApiService
